import { PaginatedApiResponse } from '@shared/models/api-response.models';
import { LocalizedCode } from '@/app/dashboard/models/dashboard.model';

/**
 * Policy List Models
 */

export enum PolicyFilterStatus {
  ALL = 'ALL',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum PolicyOrderBy {
  CREATED_AT = 'created_at',
  UPDATED_AT = 'updated_at',
  DOMAIN = 'domain',
  RESOURCE_TYPE = 'resource_type',
  ACTION = 'action',
  STATUS = 'status',
}

export type PolicyOrder = 'ASC' | 'DESC';

/**
 * Maps directly to the backend PolicyListCategory enum.
 * Each value resolves to a specific domain/resource combination server-side.
 */
export enum PolicyCategory {
  VAULT = 'VAULT',
  WALLET = 'WALLET',
  TRANSACTION = 'TRANSACTION',
  ACCOUNT = 'ACCOUNT',
  POLICY = 'POLICY',
  SYSTEM = 'SYSTEM',
  USER = 'USER',
}

export interface CreatorInfo {
  id: string;
  firstname: string;
  lastname: string;
}

export interface PendingApprovalInfo {
  requestId: string;
  requestType: string;
}

export interface PolicyStage {
  stageNo: number;
  rules: Array<{
    role: string;
    verification?: string;
    atLeastFromAll?: number;
    allOfAll?: boolean;
  }>;
  ttlMinutes: number;
}

export interface PolicyListItem {
  id: string;
  accountId: string;
  domain: LocalizedCode;
  resourceType: LocalizedCode;
  action: LocalizedCode;
  scopeType: string | null;
  scopeId: string | null;
  version: number;
  status: LocalizedCode;
  createdById: string;
  creator: CreatorInfo;
  stages: PolicyStage[];
  pendingApproval: PendingApprovalInfo | null;
  createdAt?: string;
  updatedAt?: string;
  policyAction: {
    value: {
      domain: string;
      action: string;
      resourceType: string;
      scope: string | null;
    };
    translation: string;
  };
}

export interface GetPoliciesQuery {
  page?: number;
  limit?: number;
  status?: PolicyFilterStatus;
  category?: PolicyCategory;
  orderBy?: PolicyOrderBy;
  order?: PolicyOrder;
}

/** `GET /api/v1/policies` — paginated policy list. */
export type GetPoliciesResponse = PaginatedApiResponse<PolicyListItem>;

/**
 * Domain filter tab options shown in the UI.
 * `value` maps to PolicyCategory (sent as `category` query param).
 */
export interface DomainFilter {
  value: PolicyCategory;
  label: string;
}

/** Domain filter values — labels via `policies.domain.${value}` */
export const POLICY_DOMAIN_FILTER_VALUES: PolicyCategory[] = [
  PolicyCategory.VAULT,
  PolicyCategory.WALLET,
  PolicyCategory.TRANSACTION,
  PolicyCategory.ACCOUNT,
  PolicyCategory.POLICY,
  PolicyCategory.SYSTEM,
  PolicyCategory.USER,
];

/**
 * @deprecated Use {@link POLICY_DOMAIN_FILTER_VALUES} with TranslateService.
 */
export const DOMAIN_FILTERS: DomainFilter[] = POLICY_DOMAIN_FILTER_VALUES.map(
  value => ({ value, label: value })
);
